import { LitElement, css, html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { overrideDesignTokens } from '../../shared/styles/override-design-tokens';
import { ludsBaseFont } from '../../shared/styles/typography';
import '../icon/icon';

/** Specifies the type of the tag, which determines its background and text color. */
export type LudsTagType =
  | 'progress'
  | 'positive'
  | 'warning'
  | 'alert'
  | 'neutral'
  | 'complete'
  | 'pass'
  | 'fail'
  | 'pending'
  | 'transparent'
  | 'course'
  | 'learning-path'
  | 'live-session';

@customElement('luds-tag')
export class LudsTag extends LitElement {
  /** Specifies the type of the tag, which determines its background and text color. */
  @property({ type: String, reflect: true }) type: LudsTagType = 'neutral';
  /** Specifies the size of the tag, which determines its padding. */
  @property({ type: String, reflect: true }) size: 'small' | 'large' = 'small';
  /** Specifies the content of the tag. */
  @property({ type: String, reflect: true }) content: string;
  /** Specifies the variant of the tag, which determines its shape and style. */
  @property({ type: String, reflect: true }) variant: 'square' | 'square-outline' | 'default' = 'default';
  /** Specifies the icon of the tag. */
  @property({ type: String, reflect: true }) icon: string;
  /** Specifies the custom color of the tag. */
  @property({ type: String, reflect: true }) color: string;

  static override styles = [
    overrideDesignTokens,
    ludsBaseFont,
    css`
      :host {
        --luds-tag-border-radius-xl: var(--corners-xl);
        --luds-tag-border-radius-s: var(--corners-s);

        --luds-tag-large-padding-y: var(--spacing-spacing-08);
        --luds-tag-large-padding-x: var(--spacing-spacing-12);
        --luds-tag-small-padding-y: var(--spacing-spacing-02);
        --luds-tag-small-padding-x: var(--spacing-spacing-08);

        /* Tags colors */
        --luds-tag-progress-background-color: var(--primitive-colours-blues-blue-100);
        --luds-tag-progress-text-color: var(--primitive-colours-blues-blue-900);
        --luds-tag-success-background-color: var(--primitive-colours-greens-green-100);
        --luds-tag-success-text-color: var(--colours-extended-palette-aquas-aqua-900);
        --luds-tag-warning-background-color: var(--primitive-colours-oranges-orange-100);
        --luds-tag-warning-text-color: var(--primitive-colours-reds-red-400);
        --luds-tag-alert-background-color: var(--primitive-colours-reds-red-100);
        --luds-tag-alert-text-color: var(--colours-extended-palette-reds-red-900);
        --luds-tag-neutral-background-color: var(--primitive-colours-neutrals-gray-300);
        --luds-tag-neutral-text-color: var(--primitive-colours-neutrals-gray-800);

        /* Difficulty pills */
        --luds-difficulty-tag-progress-background-color: var(--primitive-colours-blues-blue-100);
        --luds-difficulty-tag-positive-background-color: var(--primitive-colours-greens-green-200);
        --luds-difficulty-tag-warning-background-color: var(--primitive-colours-oranges-orange-100);
        --luds-difficulty-tag-alert-background-color: var(--primitive-colours-reds-red-100);
        --luds-difficulty-tag-neutral-background-color: var(--primitive-colours-neutrals-gray-300);

        /* Results pills */
        --luds-results-tag-complete-background-color: var(--primitive-colours-greens-green-300);
        --luds-results-tag-fail-background-color: var(--primitive-colours-reds-red-200);
        --luds-results-tag-pending-background-color: var(--primitive-colours-yellows-yellow-200);

        /* Card tags */
        --luds-crd-tag-course-background-color: var(--primitive-colours-blues-blue-200);
        --luds-crd-tag-learning-path-background-color: var(--primitive-colours-violets-violet-200);
        --luds-crd-tag-live-session-background-color: var(--primitive-colours-greens-green-300);

        border-radius: var(--luds-tag-border-radius-xl);
        font-feature-settings: 'cv11' on;
        font-family: 'Inter', sans-serif;
      }

      .tag {
        font-size: 12px;
        font-weight: 500;
        line-height: 16px;
        text-align: left;
      }

      .tag-icon {
        display: flex;
        align-items: center;
        gap: var(--spacing-spacing-04);
      }

      .tag-dot {
        width: 8px;
        height: 8px;
        border-radius: 2px;
        background-color: red;
      }

      .tag-outline {
        display: flex;
        align-items: center;
        gap: 8px;
      }

      :host([size='small']) {
        padding: var(--luds-tag-small-padding-y) var(--luds-tag-small-padding-x);
      }

      :host([size='large']) {
        padding: var(--luds-tag-large-padding-y) var(--luds-tag-large-padding-x);
      }

      :host([variant='default'][type='progress']) {
        background-color: var(--luds-tag-progress-background-color);
        color: var(--luds-tag-progress-text-color);
      }

      :host([variant='default'][type='positive']) {
        background-color: var(--luds-tag-success-background-color);
        color: var(--luds-tag-success-text-color);
      }

      :host([variant='default'][type='warning']) {
        background-color: var(--luds-tag-warning-background-color);
        color: var(--luds-tag-warning-text-color);
      }

      :host([variant='default'][type='alert']) {
        background-color: var(--luds-tag-alert-background-color);
        color: var(--luds-tag-alert-text-color);
      }

      :host([variant='default'][type='neutral']) {
        background-color: var(--luds-tag-neutral-background-color);
        color: var(--luds-tag-neutral-text-color);
      }

      :host([variant='square']) {
        border-radius: var(--luds-tag-border-radius-s);
        color: var(--primitive-colours-neutrals-gray-900);
        padding: var(--spacing-spacing-04) var(--spacing-spacing-08);
      }

      :host([variant='square'][type='progress']) {
        background-color: var(--luds-difficulty-tag-progress-background-color);
      }

      :host([variant='square'][type='positive']) {
        background-color: var(--luds-difficulty-tag-positive-background-color);
      }

      :host([variant='square'][type='warning']) {
        background-color: var(--luds-difficulty-tag-warning-background-color);
      }

      :host([variant='square'][type='alert']) {
        background-color: var(--luds-difficulty-tag-alert-background-color);
      }

      :host([variant='square'][type='neutral']) {
        background-color: var(--luds-difficulty-tag-neutral-background-color);
      }

      :host([variant='square'][type='complete']),
      :host([variant='square'][type='pass']) {
        background-color: var(--luds-results-tag-complete-background-color);
      }

      :host([variant='square'][type='fail']) {
        background-color: var(--luds-results-tag-fail-background-color);
      }

      :host([variant='square'][type='pending']) {
        background-color: var(--luds-results-tag-pending-background-color);
      }

      :host([variant='square'][type='transparent']) {
        background-color: transparent;
      }

      :host([variant='square-outline']) {
        padding: var(--spacing-spacing-02) var(--spacing-spacing-08);
        border: 1px solid transparent;
        border-radius: var(--luds-tag-border-radius-s);
        display: flex;
        align-items: center;
      }

      :host([variant='square-outline'][type='course']) {
        border: 1px solid var(--luds-crd-tag-course-background-color);

        .tag-dot {
          background-color: var(--luds-crd-tag-course-background-color);
        }
      }

      :host([variant='square-outline'][type='learning-path']) {
        border: 1px solid var(--luds-crd-tag-learning-path-background-color);

        .tag-dot {
          background-color: var(--luds-crd-tag-learning-path-background-color);
        }
      }

      :host([variant='square-outline'][type='live-session']) {
        border: 1px solid var(--luds-crd-tag-live-session-background-color);

        .tag-dot {
          background-color: var(--luds-crd-tag-live-session-background-color);
        }
      }
    `,
  ];

  private renderDot() {
    return html`<div class="tag-dot"></div>`;
  }

  private renderIcon(icon: string) {
    return html`<luds-icon icon="${icon}" size="extra-extra-small"></luds-icon>`;
  }

  override render() {
    const { content, icon, variant } = this;
    const tagClasses = {
      tag: true,
      'tag-icon': !!icon,
      'tag-outline': variant === 'square-outline',
    };

    return html`<div class="${classMap(tagClasses)}">
      ${variant === 'square-outline' ? this.renderDot() : nothing} ${icon ? this.renderIcon(icon) : nothing} ${content}
    </div> `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'luds-tag': LudsTag;
  }
}
